// @ts-strict-ignore
import { ApplicationRef, ComponentRef, createComponent, Directive, ElementRef, EmbeddedViewRef, EnvironmentInjector, Injector, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, TemplateRef } from '@angular/core';
import { fromEvent, Subscription } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { TooltipComponent } from './tooltip.component';
import { DeviceUtils } from '../util/device-utils';
import { Timer } from '../timer';

@Directive({
  selector: '[tooltip]'
})
export class TooltipDirective implements OnInit, OnChanges, OnDestroy {

  private static readonly TOOLTIP_TIMER_DELAY: number = 500;
  private enterSubscription: Subscription;
  private leaveSubscription: Subscription;
  private moveSubscription: Subscription;
  private touchStartSubscription: Subscription;
  private touchEndSubscription: Subscription;
  private scrollSubscription: Subscription;

  private open = false;
  private tooltipComponentRef: ComponentRef<TooltipComponent>;
  private recentTouched = false;
  private _timer: Timer = new Timer(TooltipDirective.TOOLTIP_TIMER_DELAY);

  @Input() tooltipText: string | TemplateRef<any>;
  @Input() tooltipPosition: 'top' | 'bottom' | 'left' | 'right' = 'top';
  @Input() tooltipMaxWidth = 400;
  @Input() onlyTruncate: boolean;
  @Input() tooltipDisabled: boolean = false;

  /** Make sure to sanitize any external user-provided text when setting this to true */
  @Input() allowHtml = false;
  @Input() tooltipDisabledFunction: () => boolean;
  private _show = () => this.show();

  constructor(
    private readonly elementRef: ElementRef,
    private readonly injector: Injector,
    private readonly environmentInjector: EnvironmentInjector,
    private readonly applicationRef: ApplicationRef,
    ) {
  }

  ngOnInit(): void {
    this.touchStartSubscription =
      fromEvent(this.elementRef.nativeElement, `touchstart`, { capture: true })
        .subscribe(() => {
          this.recentTouched = true;
        });
    this.touchEndSubscription =
      fromEvent(this.elementRef.nativeElement, `touchend`, { capture: true }).pipe(debounceTime(1000))
        .subscribe(() => {
          this.recentTouched = false;
        });
    this.enterSubscription =
      fromEvent(this.elementRef.nativeElement, `mouseover`, { capture: true })
        .subscribe((event) => {
          this.handleEnter(event);
        });
    this.leaveSubscription =
      fromEvent(this.elementRef.nativeElement, `mouseout`, { capture: true })
        .subscribe(() => {
          this.handleLeave();
        });
    this.moveSubscription =
      fromEvent(this.elementRef.nativeElement, `mousemove`, { capture: true })
        .subscribe(() => {
          this.handleMove();
        });
    this.scrollSubscription =
      fromEvent(document, `scroll`, { capture: true })
        .subscribe(() => {
          this.hide();
        });
    // to prevent text highlighting popup elements in mobile browsers
    if (DeviceUtils.isTouch()) {
      this.elementRef.nativeElement.style.userSelect = `none`;
      this.elementRef.nativeElement.style.webkitUserSelect = `none`;
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.tooltipDisabled && changes.tooltipDisabled.currentValue) {
      setTimeout(() => {
        this.hide();
      }, 0);
    }
  }

  ngOnDestroy(): void {
    this.hide();
    if (this.enterSubscription) {
      this.enterSubscription.unsubscribe();
    }
    if (this.leaveSubscription) {
      this.leaveSubscription.unsubscribe();
    }
    if (this.moveSubscription) {
      this.moveSubscription.unsubscribe();
    }
    if (this.touchStartSubscription) {
      this.touchStartSubscription.unsubscribe();
    }
    if (this.touchEndSubscription) {
      this.touchEndSubscription.unsubscribe();
    }
    if (this.scrollSubscription) {
      this.scrollSubscription.unsubscribe();
    }
  }

  private handleEnter(event): void {
    if (this.recentTouched) {
      return;
    }
    if (!this._timer.hasEventListener('timer')) {
      this._timer.addEventListener('timer', this._show);
    }
  }

  private handleLeave(): void {
    this.hide();
    if (this._timer.hasEventListener('timer')) {
      this._timer.reset();
      this._timer.removeEventListener('timer', this._show);
    }
  }

  private handleMove(): void {
    this.hide();
    this._timer.reset();
    this._timer.start();
  }

  private show(event?) {
    let disabled = this.tooltipDisabled;
    if (this.tooltipDisabledFunction) {
      disabled = this.tooltipDisabledFunction();
    }
    if (disabled) {
      return;
    }
    const el = this.elementRef.nativeElement;
    if (!el.isConnected || (this.onlyTruncate && el.scrollWidth <= el.clientWidth)) {
      return;
    }
    this.render(event);
  }

  private render(event?) {
    if (this.open) {
      return;
    }
    this.tooltipComponentRef = createComponent(TooltipComponent, {
      environmentInjector: this.environmentInjector,
      elementInjector: this.injector
    });
    this.tooltipComponentRef.instance.content = this.tooltipText;
    this.tooltipComponentRef.instance.placement = this.tooltipPosition;
    this.tooltipComponentRef.instance.maxWidth = this.tooltipMaxWidth;
    this.tooltipComponentRef.instance.allowHtml = this.allowHtml;
    this.positionTooltip(this.tooltipComponentRef.instance, event);
    this.applicationRef.attachView(this.tooltipComponentRef.hostView);
    const rootNodes = (this.tooltipComponentRef.hostView as EmbeddedViewRef<any>).rootNodes;
    const domElem = rootNodes[0] as HTMLElement;
    document.body.appendChild(domElem);
    this.open = true;
  }

  private hide() {
    if (!this.open) {
      return;
    }
    this.applicationRef.detachView(this.tooltipComponentRef.hostView);
    this.tooltipComponentRef.destroy();
    this.open = false;
  }

  private positionTooltip(tooltipComponent: TooltipComponent, event?): void {
    const clientReact = this.elementRef.nativeElement.getBoundingClientRect();
    let top;
    let left;

    switch (this.tooltipPosition) {
      case `top`:
        top = clientReact.top;
        left = clientReact.left + clientReact.width / 2;
        break;
      case `bottom`:
        top = clientReact.top + clientReact.height;
        left = clientReact.left + clientReact.width / 2;
        break;
      case `left`:
        top = clientReact.top + clientReact.height / 2;
        left = clientReact.left;
        break;
      case `right`:
        top = clientReact.top + clientReact.height / 2;
        left = clientReact.left + clientReact.width;
        break;
      default:
        top = 0;
        left = 0;
    }

    tooltipComponent.top = top;
    tooltipComponent.left = left;
  }
}
